import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/** A single breadcrumb in a trail. */
export interface Breadcrumb {
  label: string;
  /** Router link. Preferred over href. */
  routerLink?: string | unknown[];
  /** Plain href. */
  href?: string;
  /** Optional leading icon name. */
  icon?: string;
}

/**
 * Hierarchical navigation trail. The last item is unlinked and carries
 * `aria-current="page"`. On narrow viewports you can collapse middle items to a
 * `…` label via `collapseAfter` — the current minimal implementation just wraps.
 *
 * @example
 * <app-breadcrumbs
 *   [items]="[
 *     { label: 'Home', routerLink: '/' },
 *     { label: 'Students', routerLink: '/students' },
 *     { label: 'Ada Lovelace' }
 *   ]"
 * />
 *
 * @feature shared
 */
@Component({
  selector: 'app-breadcrumbs',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, IconComponent],
  host: { class: 'block' },
  template: `
    <nav aria-label="Breadcrumb">
      <ol class="flex flex-wrap items-center gap-1 text-sm text-muted">
        @for (item of items(); track $index; let last = $last) {
          <li class="inline-flex items-center gap-1">
            @if (last) {
              <span class="font-semibold text-body" aria-current="page">
                @if (item.icon) {
                  <app-icon [name]="item.icon" size="xxs" />
                }
                {{ item.label }}
              </span>
            } @else if (item.routerLink) {
              <a
                [routerLink]="item.routerLink"
                class="inline-flex items-center gap-1 text-link hover:text-link-hover rounded focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
              >
                @if (item.icon) {
                  <app-icon [name]="item.icon" size="xxs" />
                }
                {{ item.label }}
              </a>
            } @else {
              <a
                [href]="item.href ?? '#'"
                class="inline-flex items-center gap-1 text-link hover:text-link-hover rounded focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
              >
                @if (item.icon) {
                  <app-icon [name]="item.icon" size="xxs" />
                }
                {{ item.label }}
              </a>
            }

            @if (!last) {
              <app-icon name="chevron-forward" size="xxs" color="medium" />
            }
          </li>
        }
      </ol>
    </nav>
  `,
})
export class BreadcrumbsComponent {
  /** Ordered list of crumbs. Last item is the current page. */
  readonly items = input.required<Breadcrumb[]>();
}
